import { useEffect, useMemo, useState } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import {
  IncidentWorkItemIdSchema,
  tk,
  translate,
  type IncidentWorkItemId,
  type IncidentWorkItemWithContext,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { DateTimePicker } from "../../components/FormBuilder/DateTimePicker/DateTimePicker.js";
import { useUser } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorPage } from "../Error/Error.js";
import {
  buildEditableFields,
  buildInitialValues,
  buildPosRelinkTargets,
  computeEdits,
  editsToRequestBody,
  formatDateTime,
  tko,
  type FormValues,
  type PendingEdit,
  type PosRelinkTarget,
} from "./helpers.js";
import { RelinkPlaceOfSafetyModal } from "./RelinkPlaceOfSafetyModal.js";

// ---------------------------------------------------------------------------
// Inner component
// ---------------------------------------------------------------------------

export interface AdminEditReportingFieldsInnerProps {
  incident: IncidentWorkItemWithContext;
  onSaved: () => void;
}

export function AdminEditReportingFieldsInner({
  incident,
  onSaved,
}: AdminEditReportingFieldsInnerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notifications = useToastNotifications();
  const incidentId = incident.incidentWorkItem.id;

  const { detention, arrivals, departures } = useMemo(
    () =>
      buildEditableFields(
        incident,
        t(tk("adminEditReportingFields.placeOfSafetyFallback")),
      ),
    [incident, t],
  );

  const allFields = useMemo(
    () => [...(detention ? [detention] : []), ...arrivals, ...departures],
    [detention, arrivals, departures],
  );

  const initialValues = useMemo<FormValues>(
    () => buildInitialValues(allFields),
    [allFields],
  );

  const [pendingEdits, setPendingEdits] = useState<PendingEdit[] | null>(null);
  const [reason, setReason] = useState("");
  const [ccmNumber, setCcmNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const notSet = t(tk("adminEditReportingFields.notSet"));

  // Place of safety can only be re-linked once the incident has ended.
  const isEnded =
    incident.incidentWorkItem.status ===
    IncidentWorkItemStatusSchema.enum.ended;
  const posRelinkTargets = useMemo(
    () =>
      buildPosRelinkTargets(
        incident,
        t(tk("adminEditReportingFields.placeOfSafetyFallback")),
      ),
    [incident, t],
  );
  const targetByFormId = useMemo(
    () => new Map(posRelinkTargets.map((target) => [target.formId, target])),
    [posRelinkTargets],
  );
  const [relinkTarget, setRelinkTarget] = useState<PosRelinkTarget | null>(
    null,
  );

  const handleReview = (values: FormValues) => {
    const edits = computeEdits(allFields, values);

    if (edits.length === 0) {
      notifications.show(t(tk("adminEditReportingFields.noChanges")), {
        severity: "info",
      });
      return;
    }

    setPendingEdits(edits);
  };

  const handleConfirm = async () => {
    if (!pendingEdits || reason.trim() === "" || ccmNumber.trim() === "") {
      return;
    }

    const body = editsToRequestBody(pendingEdits, reason, ccmNumber);

    setSubmitting(true);
    try {
      const res = await api.incidentWorkItem.adminEditReportingFields(
        incidentId,
        body,
      );
      if (res.status === 204) {
        notifications.show(t(tk("adminEditReportingFields.saveSuccess")), {
          severity: "success",
        });
        onSaved();
        navigate(createRoute.incidentSummaryPage(incidentId));
      } else {
        const reasonMessage =
          typeof res.data === "object" &&
          res.data !== null &&
          "reason" in res.data
            ? String((res.data as { reason: string }).reason)
            : t(tk("adminEditReportingFields.saveError"));
        notifications.show(reasonMessage, { severity: "error" });
      }
    } catch {
      notifications.show(t(tk("adminEditReportingFields.saveError")), {
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      data-testid="admin-edit-reporting-fields-page"
      sx={{
        width: "100%",
        maxWidth: 800,
        mx: "auto",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            navigate(createRoute.incidentSummaryPage(incidentId));
          }}
          sx={{ textTransform: "none", fontWeight: 600 }}
          data-testid="back-button"
        >
          {t(tk("adminEditReportingFields.back"))}
        </Button>
      </Box>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {t(tk("adminEditReportingFields.title"))}
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t(tk("adminEditReportingFields.description"))}
      </Alert>

      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={handleReview}
        enableReinitialize
      >
        <Form>
          <Stack spacing={3}>
            {detention && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t(tk("adminEditReportingFields.detentionSection"))}
                  </Typography>
                  <DateTimePicker
                    fieldName={detention.formKey}
                    label={detention.label}
                    dateLabel={tko("adminEditReportingFields.dateLabel")}
                    timeLabel={tko("adminEditReportingFields.timeLabel")}
                    disableFuture
                    isEditable
                  />
                </CardContent>
              </Card>
            )}

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t(tk("adminEditReportingFields.arrivalSection"))}
                </Typography>
                {arrivals.length === 0 ? (
                  <Alert severity="info">
                    {t(tk("adminEditReportingFields.noPlaceOfSafetyForms"))}
                  </Alert>
                ) : (
                  <Stack spacing={3}>
                    {arrivals.map((field) => {
                      const target = targetByFormId.get(field.formId);
                      return (
                        <Stack key={field.formKey} spacing={1}>
                          <DateTimePicker
                            fieldName={field.formKey}
                            label={field.label}
                            dateLabel={tko(
                              "adminEditReportingFields.dateLabel",
                            )}
                            timeLabel={tko(
                              "adminEditReportingFields.timeLabel",
                            )}
                            disableFuture
                            isEditable
                          />
                          {isEnded && target && (
                            <Box>
                              <Button
                                size="small"
                                startIcon={<EditLocationAltIcon />}
                                onClick={() => {
                                  setRelinkTarget(target);
                                }}
                                sx={{ textTransform: "none" }}
                                data-testid={`relink-pos-btn-${field.formId}`}
                              >
                                {t(tk("adminRelinkPlaceOfSafety.launchButton"))}
                              </Button>
                            </Box>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {departures.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t(tk("adminEditReportingFields.leftSection"))}
                  </Typography>
                  <Stack spacing={3}>
                    {departures.map((field) => (
                      <DateTimePicker
                        key={field.formKey}
                        fieldName={field.formKey}
                        label={field.label}
                        dateLabel={tko("adminEditReportingFields.dateLabel")}
                        timeLabel={tko("adminEditReportingFields.timeLabel")}
                        disableFuture
                        isEditable
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                data-testid="review-changes-btn"
              >
                {t(tk("adminEditReportingFields.review"))}
              </Button>
            </Box>
          </Stack>
        </Form>
      </Formik>

      <PopupDialog
        open={pendingEdits !== null}
        onClose={() => {
          setPendingEdits(null);
        }}
        maxWidth="sm"
        fullWidth
        testId="confirm-reporting-fields-dialog"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t(tk("adminEditReportingFields.confirmTitle"))}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t(tk("adminEditReportingFields.confirmDescription"))}
          </Typography>
          <Stack spacing={1} sx={{ mb: 3 }}>
            {pendingEdits?.map(({ field, from, to }) => (
              <Typography key={field.formKey} variant="body2">
                {t(tk("adminEditReportingFields.changeRow"), {
                  label: translate(t, field.label),
                  from: formatDateTime(from, notSet),
                  to: formatDateTime(to, notSet),
                })}
              </Typography>
            ))}
          </Stack>
          <Stack spacing={2}>
            <TextField
              label={t(tk("adminEditReportingFields.reasonLabel"))}
              placeholder={t(tk("adminEditReportingFields.reasonPlaceholder"))}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              fullWidth
              multiline
              minRows={2}
              required
              data-testid="reason-input"
            />
            <TextField
              label={t(tk("adminEditReportingFields.ccmNumberLabel"))}
              placeholder={t(
                tk("adminEditReportingFields.ccmNumberPlaceholder"),
              )}
              value={ccmNumber}
              onChange={(e) => {
                setCcmNumber(e.target.value);
              }}
              fullWidth
              required
              data-testid="ccm-number-input"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => {
              setPendingEdits(null);
            }}
            color="error"
            variant="outlined"
          >
            {t(tk("adminEditReportingFields.cancel"))}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            variant="contained"
            disabled={
              submitting || reason.trim() === "" || ccmNumber.trim() === ""
            }
            data-testid="confirm-reporting-fields-btn"
          >
            {t(tk("adminEditReportingFields.confirmButton"))}
          </Button>
        </DialogActions>
      </PopupDialog>

      {relinkTarget && (
        <RelinkPlaceOfSafetyModal
          incident={incident}
          posTarget={relinkTarget}
          onClose={() => {
            setRelinkTarget(null);
          }}
          onSaved={onSaved}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Data fetcher + route guard
// ---------------------------------------------------------------------------

function AdminEditReportingFieldsDataFetcher({
  incidentId,
}: {
  incidentId: IncidentWorkItemId;
}) {
  const { user, loading: loadingUser } = useUser();
  const navigate = useNavigate();

  const [{ data: incident, loading, error }, refetch] =
    apiHooks.incidentWorkItem.get(incidentId);

  useEffect(() => {
    if (!loadingUser && !user?.isAdminSession) {
      navigate(createRoute.home());
    }
  }, [loadingUser, user, navigate]);

  if (loadingUser || loading) {
    return (
      <Stack
        sx={{
          width: "100%",
          maxWidth: 800,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          gap: 3,
        }}
      >
        <Skeleton variant="rounded" height={48} width={180} />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  if (error || !incident || !user?.isAdminSession) {
    return <ErrorPage />;
  }

  // The curated places of safety are provided once per session by
  // `PlacesOfSafetyProvider` in the app shell, so the picker in the relink
  // modal reads them from context without this page fetching them.
  return (
    <AdminEditReportingFieldsInner
      incident={incident}
      onSaved={() => void refetch()}
    />
  );
}

export function AdminEditReportingFieldsPage() {
  const params = useParams();
  const parseResult = IncidentWorkItemIdSchema.safeParse(params.id);

  if (!parseResult.success) {
    return <ErrorPage />;
  }

  return <AdminEditReportingFieldsDataFetcher incidentId={parseResult.data} />;
}
